/**
 * Collect deployed application URL before IT Deployment → Release.
 */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import '../AIGarage.css';

const DeployedUrlModal = ({
  isOpen,
  initialUrl = '',
  initialNote = '',
  onClose,
  onConfirm,
}) => {
  const [url, setUrl] = useState(initialUrl || '');
  const [note, setNote] = useState(initialNote || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setUrl(initialUrl || '');
    setNote(initialNote || '');
    setError(null);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialUrl, initialNote]);

  useEffect(() => {
    if (!isOpen || submitting) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (url || '').trim();
    if (!trimmed) {
      setError('Deployed application URL is required.');
      return;
    }
    try {
      // Basic URL check
      // eslint-disable-next-line no-new
      new URL(trimmed);
    } catch {
      setError('Enter a valid URL (including https://).');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await onConfirm?.({ deployedUrl: trimmed, note: note.trim() || undefined });
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'string' && detail) ||
          err?.message ||
          'Could not advance to Release.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="garage-modal-overlay" onClick={submitting ? undefined : onClose}>
      <div className="garage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <h2>Deployed application URL</h2>
          <button
            type="button"
            className="garage-modal-close"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={20} />
          </button>
        </div>
        <div className="garage-modal-body">
          <p className="garage-request-info-context">
            Provide the live URL for the deployed app before moving to Release.
          </p>
          <form onSubmit={handleSubmit}>
            <label className="garage-form-label" htmlFor="deployed-app-url">
              Application URL <span aria-hidden>*</span>
            </label>
            <input
              id="deployed-app-url"
              className="garage-input"
              type="url"
              required
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={submitting}
            />
            <label
              className="garage-form-label"
              htmlFor="deployed-app-note"
              style={{ marginTop: '0.75rem' }}
            >
              Note (optional)
            </label>
            <textarea
              id="deployed-app-note"
              className="garage-textarea"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
            />
            {error ? <p className="garage-form-error">{error}</p> : null}
            <div className="garage-modal-actions" style={{ marginTop: '1rem' }}>
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="garage-btn garage-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Saving…' : 'Go to Release'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default DeployedUrlModal;
